import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs } from "../../api/examApi";
import { listStudents } from "../../api/studentApi";
import { listExamRoutineGenerate } from "../../api/examRoutineGenerateApi";
import "../Attendance/Attendance.css";
import "../Routine/ClassRoutine.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");

/**
 * Direct port of digicampusbd's `exam-attendance-sheet` — a blank attendance
 * sheet to print and use on paper during an exam: real students in a
 * section down the rows, one real column per that exam's scheduled date +
 * subject (from `ExamRoutineGenerate`, built earlier this session), left
 * empty for the invigilator to mark by hand, plus a signature row exactly
 * like the old app's own printout. Reuses `GET /api/students` and
 * `GET /api/exam-routine-generate` — no new backend work.
 */
export default function ExamAttendanceSheetPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examId, setExamId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);

  const [students, setStudents] = useState([]);
  const [routineColumns, setRoutineColumns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    groupApi.list().then(setGroups);
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamId(configs.length > 0 ? String(configs[0].examId) : "");
    });
  }, [classConfigId, classConfigs]);

  const toggleGroup = (id) => {
    setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  const handleGenerate = async () => {
    setError("");
    setRoutineColumns(null);
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc || !academicYear || !examId || groupIds.length === 0) {
      setError("Please select section, exam, and at least one group.");
      return;
    }
    setLoading(true);
    try {
      const [studentList, routineList] = await Promise.all([
        listStudents(academicYear, classConfigId),
        listExamRoutineGenerate(academicYear, examId, cc.classId, groupIds),
      ]);
      setStudents(studentList.filter((s) => groupIds.includes(String(s.groupId))).sort((a, b) => a.studentRoll - b.studentRoll));
      if (routineList.length === 0) {
        setError("Routine has not yet been created!");
      }
      setRoutineColumns(routineList);
    } catch (err) {
      setError("Failed to load attendance sheet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>Exam Attendance Sheet</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters no-print">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Exam
          <select value={examId} onChange={(e) => setExamId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examId} value={ec.examId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row no-print">
        <span className="admit-card-label">Select Group:</span>{" "}
        {groups.map((g) => (
          <label key={g.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={groupIds.includes(String(g.id))} onChange={() => toggleGroup(String(g.id))} />
            {g.name}
          </label>
        ))}
        <button type="button" className="btn-primary" onClick={handleGenerate} style={{ marginLeft: 12 }}>
          Generate
        </button>
        {routineColumns && routineColumns.length > 0 && (
          <button type="button" className="btn-secondary" onClick={() => window.print()} style={{ marginLeft: 8 }}>
            Print
          </button>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        routineColumns &&
        routineColumns.length > 0 && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Roll No</th>
                  <th>Student Name</th>
                  {routineColumns.map((r) => (
                    <th key={r.examRoutineId}>
                      {r.examDate}
                      <br />
                      {r.subjectName}
                    </th>
                  ))}
                </tr>
                <tr>
                  <td colSpan={3} style={{ textAlign: "right", fontWeight: "bold" }}>
                    Exam invigilator Signature
                  </td>
                  {routineColumns.map((r) => (
                    <td key={r.examRoutineId}></td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.identificationId}>
                    <td>{s.customStudentId}</td>
                    <td>{s.studentRoll}</td>
                    <td>{s.studentName}</td>
                    {routineColumns.map((r) => (
                      <td key={r.examRoutineId}></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {!loading && routineColumns && routineColumns.length > 0 && students.length === 0 && <p>No students found for this selection.</p>}
    </MainLayout>
  );
}
